import {
  type CreateCruiseRequest,
  type CruiseCabinRateListQuery,
  type CruiseDetail,
  type CruisePhoto,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzCard,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzTabs,
  UzTextarea,
  UzTooltip,
} from '@utazzo/ui';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { CabinsRatesTab } from '../components/CabinsRatesTab';
import { CruiseSetupCard } from '../components/CruiseOverviewTab';
import { useCreateCruise, useCruiseDetail, useCruiseRates, useUpdateCruise } from '../use-cruises';

import { CityPicker, CountryPicker, SupplierPicker } from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { StarRatingInput } from '@/components/star-rating';
import { useCurrentUser } from '@/features/auth/use-auth';
import { AmenityGrid } from '@/features/hotels/components/AmenityGrid';
import { HotelPhotosFields } from '@/features/hotels/components/HotelPhotosCard';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useSaveShortcut } from '@/hooks/use-save-shortcut';

/**
 * Cruise editor (Shopify-style catalog layout) - the cruise analogue of
 * `HotelEditPage`. Cruise is a separate entity (own master/cabin/rate
 * tables, no TBO mirror), but the editor composes the same shared UI
 * primitives + pickers the hotel screen uses.
 *
 * Three tabs:
 *   Details         main column (identity, location, policies) + right
 *                   sidebar (setup checklist, status, commercial). One
 *                   scroll, no tab-hunting.
 *   Content & media short description, long description, amenities, photos.
 *   Cabins & rates  cabin categories with their sailing rate cards nested
 *                   inside. Locked until the cruise is first saved.
 *
 * Routes:
 *   /cruises/new          create (Details only until saved)
 *   /cruises/:id/edit     edit
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const MARKUP_OPTIONS = [
  { value: '', label: 'No markup' },
  { value: 'PERCENT', label: 'Percent' },
  { value: 'FLAT', label: 'Flat amount' },
];

const RATES_QUERY: CruiseCabinRateListQuery = { page: 1, pageSize: 200, sortDir: 'asc' };

interface CruiseFormValues {
  name: string;
  slug: string | null;
  starRating: number;
  cruiseLine: string | null;
  countryId: string | null;
  cityId: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  shortDescription: string | null;
  longDescriptionHtml: string | null;
  amenities: string[];
  photos: CruisePhoto[];
  embarkTime: string | null;
  disembarkTime: string | null;
  policiesHtml: string | null;
  vendorSupplierId: string | null;
  contractRef: string | null;
  markupKind: 'PERCENT' | 'FLAT' | null;
  markupValue: number;
  status: CruiseDetail['status'];
}

const CREATE_DEFAULTS: CruiseFormValues = {
  name: '',
  slug: null,
  starRating: 0,
  cruiseLine: null,
  countryId: null,
  cityId: null,
  address: null,
  latitude: null,
  longitude: null,
  shortDescription: null,
  longDescriptionHtml: null,
  amenities: [],
  photos: [],
  embarkTime: null,
  disembarkTime: null,
  policiesHtml: null,
  vendorSupplierId: null,
  contractRef: null,
  markupKind: null,
  markupValue: 0,
  status: 'DRAFT',
};

const parseAmenities = (a: unknown): string[] =>
  Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : [];

const parsePhotos = (a: unknown): CruisePhoto[] => {
  if (!Array.isArray(a)) return [];
  return a.filter(
    (p): p is CruisePhoto =>
      Boolean(p) &&
      typeof p === 'object' &&
      (typeof (p as { url?: unknown }).url === 'string' ||
        typeof (p as { mediaId?: unknown }).mediaId === 'string'),
  );
};

const trimOrNull = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
};

/** URL slug from free text: lowercase, alphanumerics, hyphen-separated. */
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** "HH:mm" -> a Date carrying only that time-of-day (for UzDateTimePicker). */
const hhmmToDate = (hhmm: string | null | undefined): Date | null => {
  if (!hhmm) return null;
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

/** Date -> "HH:mm" (24-hour, zero-padded). */
const dateToHhmm = (d: Date | null): string | null =>
  d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : null;

const toFormValues = (cruise: CruiseDetail | null): CruiseFormValues => {
  if (!cruise) return { ...CREATE_DEFAULTS };
  return {
    name: cruise.name,
    slug: cruise.slug,
    starRating: cruise.starRating,
    cruiseLine: cruise.cruiseLine,
    countryId: cruise.countryId,
    cityId: cruise.cityId,
    address: cruise.address,
    latitude: cruise.latitude,
    longitude: cruise.longitude,
    shortDescription: cruise.shortDescription,
    longDescriptionHtml: cruise.longDescriptionHtml,
    amenities: parseAmenities(cruise.amenities),
    photos: parsePhotos(cruise.photos),
    embarkTime: cruise.embarkTime,
    disembarkTime: cruise.disembarkTime,
    policiesHtml: cruise.policiesHtml,
    vendorSupplierId: cruise.vendorSupplierId,
    contractRef: cruise.contractRef,
    markupKind: cruise.markupKind,
    markupValue: cruise.markupValue,
    status: cruise.status,
  };
};

const fromFormValues = (values: CruiseFormValues): CreateCruiseRequest => ({
  name: values.name.trim(),
  slug: trimOrNull(values.slug),
  starRating: values.starRating,
  cruiseLine: trimOrNull(values.cruiseLine),
  countryId: values.countryId ?? null,
  cityId: values.cityId ?? null,
  address: trimOrNull(values.address),
  latitude: values.latitude,
  longitude: values.longitude,
  shortDescription: trimOrNull(values.shortDescription),
  longDescriptionHtml: trimOrNull(values.longDescriptionHtml),
  amenities: values.amenities.length > 0 ? values.amenities : null,
  photos: values.photos.length > 0 ? values.photos : null,
  embarkTime: trimOrNull(values.embarkTime),
  disembarkTime: trimOrNull(values.disembarkTime),
  policiesHtml: trimOrNull(values.policiesHtml),
  vendorSupplierId: values.vendorSupplierId ?? null,
  contractRef: trimOrNull(values.contractRef),
  markupKind: values.markupKind,
  markupValue: values.markupValue ?? 0,
  status: values.status,
});

export const CruiseEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const canApprove = me?.role === 'ADMIN';
  const isCreate = !id || id === 'new';
  const [activeTab, setActiveTab] = useState<string>('details');

  const detailQuery = useCruiseDetail(isCreate ? null : (id ?? null));
  const cruise = detailQuery.data ?? null;
  const ratesQuery = useCruiseRates(isCreate ? null : (id ?? null), RATES_QUERY);
  const rates = ratesQuery.data?.data ?? [];

  const createCruise = useCreateCruise();
  const updateCruise = useUpdateCruise();
  const pending = createCruise.isPending || updateCruise.isPending;

  const form = useForm<CruiseFormValues>({ defaultValues: toFormValues(null) });

  useEffect(() => {
    if (isCreate) form.reset(toFormValues(null));
    else if (cruise) form.reset(toFormValues(cruise));
  }, [isCreate, cruise, form]);

  const submit = form.handleSubmit(async (values) => {
    // Slug is mandatory. Operators can auto-generate it from the name with the
    // wand button, but it must be set before we save.
    if (!trimOrNull(values.slug)) {
      form.setError('slug', { type: 'required', message: 'URL slug is required' });
      setActiveTab('details');
      return;
    }
    const payload = fromFormValues(values);
    if (isCreate) {
      const created = await createCruise.mutateAsync(payload);
      navigate(`/cruises/${created.id}/edit`);
    } else if (cruise) {
      await updateCruise.mutateAsync({ id: cruise.id, payload });
    }
  });

  // Cmd/Ctrl+S saves (validates + highlights errors, same as the Save button).
  useSaveShortcut(() => {
    if (!pending) void submit();
  });

  const tabs = useMemo(() => {
    const list: { value: string; label: ReactNode; content: ReactNode; disabled?: boolean }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              {/* Main column - core identity only. Content/media live on
                  their own tab to keep this scannable. */}
              <div className="min-w-0 space-y-6">
                <IdentityCard form={form} pending={pending} />
                <LocationCard form={form} pending={pending} />
                <PoliciesCard pending={pending} />
              </div>

              {/* Sidebar - sticks just below the top bar on scroll. */}
              <aside className="space-y-6 self-start lg:sticky lg:top-8">
                {!isCreate && cruise && (
                  <CruiseSetupCard
                    cruise={cruise}
                    rates={rates}
                    ratesLoading={ratesQuery.isLoading}
                    onGoToCabins={() => setActiveTab('cabins')}
                  />
                )}
                <StatusCard pending={pending} canApprove={canApprove} />
                <CommercialCard form={form} pending={pending} />
              </aside>
            </div>
          </UzForm>
        ),
      },
      {
        value: 'content',
        label: 'Content & media',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <DescriptionCard pending={pending} />
              <MediaCard pending={pending} />
            </div>
          </UzForm>
        ),
      },
    ];
    // Always show the Cabins tab; disabled until the cruise exists (you
    // can't attach cabins to an unsaved cruise). Tooltip nudges the agent to
    // save first.
    const cabinsLocked = isCreate || !cruise;
    list.push({
      value: 'cabins',
      disabled: cabinsLocked,
      label: (
        <span
          className="inline-flex items-center gap-1.5"
          title={
            cabinsLocked ? 'Add and save the cruise details first to manage cabins.' : undefined
          }
        >
          Cabins & rates
          {cruise && cruise.cabins.length > 0 && (
            <span className="text-[10px] text-zinc-400 tabular-nums">{cruise.cabins.length}</span>
          )}
        </span>
      ),
      content:
        !isCreate && cruise ? (
          <CabinsRatesTab
            cruiseId={cruise.id}
            cabins={cruise.cabins}
            rates={rates}
            ratesLoading={ratesQuery.isLoading}
            disabled={pending}
          />
        ) : (
          <UzAlert tone="info">
            Save the cruise details first, then add cabin categories & rates here.
          </UzAlert>
        ),
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit is stable per render cycle
  }, [isCreate, cruise, rates, ratesQuery.isLoading, canApprove, form, pending]);

  const statusDot =
    cruise?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : cruise?.status === 'DRAFT' || cruise?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    cruise?.status === 'ACTIVE'
      ? 'Active'
      : cruise?.status === 'DRAFT'
        ? 'Draft'
        : cruise?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : cruise?.status === 'INACTIVE'
            ? 'Inactive'
            : cruise?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={() => navigate('/cruises')}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to cruises"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Cruises</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add cruise' : cruise ? cruise.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && cruise && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={() => navigate('/cruises')} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && detailQuery.isLoading && <CatalogEditorSkeleton />}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">Failed to load cruise detail. Go back and retry.</UzAlert>
        )}

        {(isCreate || cruise) && (
          <UzTabs value={activeTab} onValueChange={setActiveTab} tabs={tabs} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main column cards
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}
interface FormCardProps extends CardProps {
  form: UseFormReturn<CruiseFormValues>;
}

const IdentityCard = ({ form, pending }: FormCardProps): React.JSX.Element => (
  <UzCard title="Identity">
    <div className="space-y-5">
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Cruise name"
            placeholder="e.g. Costa Serena, Symphony of the Seas"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="starRating">
          {({ field }) => (
            <StarRatingInput
              value={(field.value as number) ?? 0}
              onChange={(v) => field.onChange(v)}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="slug">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              label="URL slug"
              required
              error={error}
              disabled={pending}
              rightAdornment={
                <UzTooltip label="Auto-generate slug">
                  <button
                    type="button"
                    disabled={pending}
                    aria-label="Auto-generate slug"
                    onClick={() => {
                      const name = (form.getValues('name') ?? '').trim();
                      if (name) field.onChange(slugify(name));
                    }}
                    className="text-zinc-400 transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </UzTooltip>
              }
            />
          )}
        </UzFormField>
        <UzFormField name="cruiseLine">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              label="Cruise line"
              placeholder="e.g. Costa, Royal Caribbean"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
    </div>
  </UzCard>
);

const LocationCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const countryId = form.watch('countryId');
  return (
    <UzCard title="Homeport" description="Where sailings for this cruise embark from.">
      <div className="space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <UzFormField name="countryId">
            {({ field, error }) => (
              <CountryPicker
                value={(field.value as string | null | undefined) ?? null}
                onValueChange={(v) => {
                  field.onChange(v);
                  if (v !== countryId) form.setValue('cityId', null);
                }}
                disabled={pending}
                error={error}
              />
            )}
          </UzFormField>
          <UzFormField name="cityId">
            {({ field, error }) => (
              <CityPicker
                label="Homeport city"
                value={(field.value as string | null | undefined) ?? null}
                onValueChange={(v) => field.onChange(v)}
                countryId={countryId ?? null}
                disabled={pending}
                error={error}
              />
            )}
          </UzFormField>
        </div>
        <UzFormField name="address">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              label="Terminal / address"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <div className="grid gap-5 sm:grid-cols-2">
          <UzFormField name="latitude">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label="Latitude"
                precision={6}
                value={
                  field.value === null || field.value === undefined ? '' : (field.value as number)
                }
                onValueChange={(v) => field.onChange(v ?? null)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="longitude">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label="Longitude"
                precision={6}
                value={
                  field.value === null || field.value === undefined ? '' : (field.value as number)
                }
                onValueChange={(v) => field.onChange(v ?? null)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </div>
    </UzCard>
  );
};

const DescriptionCard = ({ pending }: CardProps): React.JSX.Element => (
  <UzCard title="Description">
    <div className="space-y-5">
      <UzFormField name="shortDescription">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Short description"
            placeholder="One-sentence summary for picker cards."
            rows={2}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="longDescriptionHtml">
        {({ field }) => (
          <RichTextEditor
            label="Long description"
            placeholder="Multi-paragraph description with formatting..."
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(html) => field.onChange(html === '' ? null : html)}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </UzCard>
);

const MediaCard = ({ pending }: CardProps): React.JSX.Element => (
  <>
    <UzCard
      title="Amenities"
      description="Tick what the ship offers. Shown as chips on cards and the docket."
    >
      <UzFormField name="amenities">
        {({ field }) => (
          <AmenityGrid
            value={(field.value as string[]) ?? []}
            onChange={(next) => field.onChange(next)}
            disabled={pending}
          />
        )}
      </UzFormField>
    </UzCard>
    <UzCard
      title="Photos"
      description="Uploaded straight to storage - same flow as custom pages. The cover fronts every card."
    >
      <UzFormField name="photos">
        {({ field }) => (
          <HotelPhotosFields
            photos={(field.value as CruisePhoto[]) ?? []}
            onChange={(next) => field.onChange(next)}
            disabled={pending}
          />
        )}
      </UzFormField>
    </UzCard>
  </>
);

const PoliciesCard = ({ pending }: CardProps): React.JSX.Element => (
  <UzCard title="Policies & timings">
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="embarkTime">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="time"
              hourFormat="24"
              label="Embark time"
              value={hhmmToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToHhmm(d))}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="disembarkTime">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="time"
              hourFormat="24"
              label="Disembark time"
              value={hhmmToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToHhmm(d))}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      <UzFormField name="policiesHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Policies"
            placeholder="Cancellation, boarding, child policy, ..."
            rows={5}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </UzCard>
);

// ---------------------------------------------------------------------------
// Sidebar cards
// ---------------------------------------------------------------------------

interface StatusCardProps extends CardProps {
  canApprove: boolean;
}

const StatusCard = ({ pending, canApprove }: StatusCardProps): React.JSX.Element => (
  <UzCard title="Status">
    <UzFormField name="status">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Lifecycle"
          options={
            canApprove
              ? STATUS_OPTIONS
              : STATUS_OPTIONS.filter((o) => o.value === 'DRAFT' || o.value === 'PENDING_APPROVAL')
          }
          value={(field.value as string | undefined) || 'DRAFT'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </UzCard>
);

const CommercialCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const markupKind = form.watch('markupKind');
  return (
    <UzCard title="Commercial">
      <div className="space-y-5">
        <UzFormField name="vendorSupplierId">
          {({ field, error }) => (
            <SupplierPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => field.onChange(v)}
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="contractRef">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              label="Contract reference"
              placeholder="e.g. C-2026-001"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="markupKind">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Markup type"
              options={MARKUP_OPTIONS}
              value={(field.value as string | null | undefined) ?? ''}
              onValueChange={(v) => field.onChange(v ? (v as 'PERCENT' | 'FLAT') : null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        {markupKind && (
          <UzFormField name="markupValue">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label={markupKind === 'PERCENT' ? 'Markup %' : 'Markup amount'}
                precision={2}
                min={0}
                leftAdornment={
                  markupKind === 'FLAT' ? (
                    <span className="text-sm text-zinc-500">₹</span>
                  ) : undefined
                }
                rightAdornment={
                  markupKind === 'PERCENT' ? (
                    <span className="text-sm text-zinc-500">%</span>
                  ) : undefined
                }
                value={(field.value as number | undefined) || ''}
                onValueChange={(v) => field.onChange(v ?? 0)}
                hint={
                  markupKind === 'PERCENT'
                    ? 'Applied on the net rate at quote time'
                    : 'Flat amount added per quote'
                }
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        )}
      </div>
    </UzCard>
  );
};
